import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { Bold, Italic, Underline as U, Heading2, Heading3, List, ListOrdered, Link2, Pilcrow, CornerDownLeft, Undo2, Redo2 } from "lucide-react";

function Btn({ on, active, children, label }) {
    return (
        <button type="button" onClick={on} title={label} aria-label={label} data-testid={`rte-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`p-1.5 rounded hover:bg-white ${active ? "bg-white text-lnl-green shadow-sm" : "text-lnl-charcoal"}`}>{children}</button>
    );
}

export default function RichTextEditor({ value, onChange, variables = [], testid = "rte" }) {
    const editor = useEditor({
        extensions: [StarterKit, Underline, Link.configure({ openOnClick: false, autolink: false })],
        content: value || "",
        onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
        editorProps: { attributes: { class: "lnl-rte min-h-[220px] p-3 text-sm focus:outline-none" } },
    });
    if (!editor) return null;
    const c = () => editor.chain().focus();
    function setLink() {
        const prev = editor.getAttributes("link").href || "https://";
        const url = window.prompt("Link address (must start with https://)", prev);
        if (url === null) return;
        if (!url) return c().unsetLink().run();
        c().extendMarkRange("link").setLink({ href: url }).run();
    }
    return (
        <div className="rounded-md border border-lnl-border bg-white" data-testid={testid}>
            <div className="flex flex-wrap items-center gap-0.5 border-b border-lnl-border bg-lnl-off-white px-2 py-1">
                <Btn label="Bold" on={() => c().toggleBold().run()} active={editor.isActive("bold")}><Bold className="h-4 w-4" /></Btn>
                <Btn label="Italic" on={() => c().toggleItalic().run()} active={editor.isActive("italic")}><Italic className="h-4 w-4" /></Btn>
                <Btn label="Underline" on={() => c().toggleUnderline().run()} active={editor.isActive("underline")}><U className="h-4 w-4" /></Btn>
                <Btn label="Heading" on={() => c().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })}><Heading2 className="h-4 w-4" /></Btn>
                <Btn label="Subheading" on={() => c().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })}><Heading3 className="h-4 w-4" /></Btn>
                <Btn label="Paragraph" on={() => c().setParagraph().run()} active={editor.isActive("paragraph")}><Pilcrow className="h-4 w-4" /></Btn>
                <Btn label="Bullet list" on={() => c().toggleBulletList().run()} active={editor.isActive("bulletList")}><List className="h-4 w-4" /></Btn>
                <Btn label="Numbered list" on={() => c().toggleOrderedList().run()} active={editor.isActive("orderedList")}><ListOrdered className="h-4 w-4" /></Btn>
                <Btn label="Link" on={setLink} active={editor.isActive("link")}><Link2 className="h-4 w-4" /></Btn>
                <Btn label="Line break" on={() => c().setHardBreak().run()}><CornerDownLeft className="h-4 w-4" /></Btn>
                <Btn label="Undo" on={() => c().undo().run()}><Undo2 className="h-4 w-4" /></Btn>
                <Btn label="Redo" on={() => c().redo().run()}><Redo2 className="h-4 w-4" /></Btn>
                {variables.length > 0 && (
                    <select value="" onChange={(e) => e.target.value && c().insertContent(`{{${e.target.value}}}`).run()}
                        className="ml-auto text-xs rounded border border-lnl-border bg-white px-2 py-1" data-testid={`${testid}-insert-variable`}>
                        <option value="">Insert Variable…</option>
                        {variables.map((v) => <option key={v} value={v}>{`{{${v}}}`}</option>)}
                    </select>
                )}
            </div>
            <EditorContent editor={editor} />
        </div>
    );
}
